import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import reorder from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/Reorder";
import updateSearchFields from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/UpdateSearchFields";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import OrderDetailPageTypeLink from "@insite/content-library/Components/OrderDetailPageTypeLink";
import { OrderHistoryPageContext } from "@insite/content-library/Pages/OrderHistoryPage";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { ClickableProps } from "@insite/mobius/Clickable";
import DataTable, { DataTableProps, SortOrderOptions } from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import HistoryContext, { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { IdentityDataViewContext } from "@TKCCP/Store/Data/PunchoutIdentity/PunchoutIdentitySelector";
import ApplicationStateTKCCP from "@TKCCP/Store/TKCCPState";
import identity from "lodash/identity";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationStateTKCCP) => {
    return {
        parameter: state.pagePunchoutIdentity.getPunchoutParameter,
    };
};

const mapDispatchToProps = {
    // updateSearchFields,
    // reorder,
};

type Props = ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory;

export interface PunchoutIdentityTableStyles {
    container?: InjectableCss;
    headerClickable?: ClickableProps;
    headerText?: TypographyProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyProps;
    userNameHeader?: DataTableHeaderProps;
    emailAddressHeader?: DataTableHeaderProps;
    activeStatusHeader?: DataTableHeaderProps;
    customerNumberHeader?: DataTableHeaderProps;
    actionHeader?: DataTableHeaderProps;
    userNameCells?: DataTableCellProps;
    emailAddressCells?: DataTableCellProps;
    statusCells?: DataTableCellProps;
    customerNumberCells?: DataTableCellProps;
    addButton?: ButtonPresentationProps;
    dataTable?: DataTableProps;
}

export const PunchoutIdentityTableStyles: PunchoutIdentityTableStyles = {
    container: {
        css: css`
            overflow: auto;
            margin-top: 0;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    noResultsContainer: {
        css: css`
            text-align: center;
            padding: 20px;
        `,
    },
    noResultsText: {
        variant: "h4",
    },
    headerClickable: {
        css: css`
            width: 100%;
            justify-content: space-between;
        `,
    },
    addButton: {
        color: "secondary",
        sizeVariant: "medium",
    },
    customerNumberCells: {
        css: css`
            width: 30%;
            white-space: inherit;
        `,
    },
    dataTable: {
        css: css`
            td {
                padding: 4px 12px;
            }
        `,
    },
};

const styles = PunchoutIdentityTableStyles;

class PunchoutIdentityTable extends React.Component<Props> {
    static contextType = IdentityDataViewContext;
    context!: React.ContextType<typeof IdentityDataViewContext>;

    identityHandler = (userId: string) => {
        const queryString = `userid=${userId}`;
        this.props.history.push(`/myaccount/addpunchoutidentitypage?${queryString}`);
    };

    render() {
        const identitesDataView = this.context;
        if (identitesDataView.isLoading) {
            return (
                <StyledWrapper {...styles.centeringWrapper}>
                    <LoadingSpinner {...styles.spinner} />
                </StyledWrapper>
            );
        }

        if (!identitesDataView.value) {
            return null;
        }

        if (identitesDataView.value.length === 0) {
            return (
                <StyledWrapper {...styles.noResultsContainer}>
                    <Typography as="p" {...styles.noResultsText} data-test-selector="orderHistoryTable_noOrdersFound">
                        {translate("No identity found")}
                    </Typography>
                </StyledWrapper>
            );
        }

        const rows = identitesDataView.value.map(identity => {
            return {
                id: identity.id,
                isActive: identity.isActive,
                userEmailAddress: identity.userEmailAddress,
                userFirstName: identity.userFirstName,
                userLastName: identity.userLastName,
                userCustomerNumbers: identity.userCustomerNumbers,
            };
        });

        return (
            <StyledWrapper {...styles.container} data-test-selector="punchoutIdentityTable">
                <DataTable {...styles.dataTable}>
                    <DataTableHead>
                        <DataTableHeader {...styles.userNameHeader} title={translate("User")}>
                            {translate("User")}
                        </DataTableHeader>
                        <DataTableHeader {...styles.emailAddressHeader}>{translate("Email Address")}</DataTableHeader>
                        <DataTableHeader {...styles.activeStatusHeader}>{translate("Active")}</DataTableHeader>
                        <DataTableHeader {...styles.customerNumberHeader}>
                            {translate("Customer Number")}
                        </DataTableHeader>
                        <DataTableHeader {...styles.actionHeader}>{translate("Action")}</DataTableHeader>
                    </DataTableHead>
                    <DataTableBody data-test-selector="punchoutIdentityTable_tableBody">
                        {rows.map(
                            ({ id, userCustomerNumbers, userEmailAddress, userFirstName, userLastName, isActive }) => (
                                <DataTableRow key={id}>
                                    <DataTableCell
                                        {...styles.userNameCells}
                                        data-test-selector="punchoutIdentityTable_tableCell_userName"
                                    >
                                        {`${userFirstName} ${userLastName}`}
                                    </DataTableCell>
                                    <DataTableCell
                                        {...styles.emailAddressCells}
                                        data-test-selector="punchoutIdentityTable_tableCell_emailAddress"
                                    >
                                        {userEmailAddress}
                                    </DataTableCell>
                                    <DataTableCell {...styles.statusCells}>
                                        {isActive ? "Active" : "InActive"}
                                    </DataTableCell>
                                    <DataTableCell
                                        {...styles.customerNumberCells}
                                        data-test-selector="orderHistoryTable_tableCell_customerNumber">
                                        {userCustomerNumbers}
                                    </DataTableCell>
                                    <DataTableCell>
                                        {isActive && (
                                            <Button {...styles.addButton} onClick={() => this.identityHandler(id)}>
                                                {translate("Add Identity")}
                                            </Button>
                                        )}
                                    </DataTableCell>
                                </DataTableRow>
                            ),
                        )}
                    </DataTableBody>
                </DataTable>
            </StyledWrapper>
        );
    }
}
const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(PunchoutIdentityTable))),
    definition: {
        group: "Order History",
        displayName: "PunchoutIdentityTable",
    },
};

export default widgetModule;
